import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { FormRecordDto } from '../../../../dtos/patientmr/forms.dto';
import { FormService } from '../../../../services/patientmr/forms.service';
import { paCategoryDiscipline, therapyDisciplines } from '../../../../constants/disciplines';

interface FormAssociationSectionProps {
  patientId: string;
  linkedFormId: string;
  linkedFormType: string;
  onChange: (field: string, value: string | number | null) => void;
  readOnly?: boolean;
  defaultOpen?: boolean;
  /** Phase 20C: a PT / OT / ST PA links its F00139 instead of a CCP form */
  serviceCategory?: string;
}

const CCP_TYPES = new Set(['CCP_PA_REQUEST', 'CCP_PA_REAUTH']);
const THERAPY_TYPES = new Set(['TX_THERAPIES_PA']);

/** A PA category billed by PT, OT or ST */
export const isTherapyPaCategory = (category: string | null | undefined): boolean =>
  therapyDisciplines().includes(paCategoryDiscipline(category) || '');

function formatTypeLabel(formType: string): string {
  if (formType === 'CCP_PA_REQUEST') return 'CCP PA Request';
  if (formType === 'CCP_PA_REAUTH') return 'CCP PA Reauthorization';
  if (formType === 'TX_THERAPIES_PA') return 'F00139 Texas Therapies PA Request';
  return formType;
}

export const FormAssociationSection: React.FC<FormAssociationSectionProps> = ({
  patientId,
  linkedFormId,
  linkedFormType,
  onChange,
  readOnly = false,
  defaultOpen = true,
  serviceCategory
}) => {
  const therapy = isTherapyPaCategory(serviceCategory);
  const allowed = therapy ? THERAPY_TYPES : CCP_TYPES;
  const [open, setOpen] = React.useState(defaultOpen);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [forms, setForms] = React.useState<FormRecordDto[]>([]);

  React.useEffect(() => {
    let mounted = true;

    const loadForms = async () => {
      if (!patientId) return;
      setLoading(true);
      setError(null);
      try {
        const allForms = await FormService.list(patientId);
        const filtered = allForms.filter(f => allowed.has(f.formType));
        if (mounted) {
          setForms(filtered);
        }
      } catch (e: any) {
        if (mounted) {
          setError(e?.message || 'Failed to load forms');
          setForms([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadForms();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, therapy]);

  const selected = forms.find(f => f.formId === linkedFormId);
  const selectedLabel = selected
    ? `${formatTypeLabel(selected.formType)} (${selected.status})`
    : (linkedFormId ? `${formatTypeLabel(linkedFormType)} (${linkedFormId})` : 'None');

  const handleSelect = (formId: string) => {
    if (!formId) {
      onChange('linkedFormId', '');
      onChange('linkedFormType', '');
      return;
    }
    const found = forms.find(f => f.formId === formId);
    onChange('linkedFormId', formId);
    onChange('linkedFormType', found?.formType || '');
  };

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left bg-white hover:bg-slate-50"
        type="button"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span className="text-sm font-semibold text-slate-800">Associated Form</span>
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-2">
          {readOnly ? (
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Linked Form</p>
              <p className="text-sm text-slate-900 mt-0.5">{selectedLabel}</p>
            </div>
          ) : (
            <>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">
                {therapy ? 'Select F00139 form' : 'Select CCP PA Form'}
              </label>
              <select
                value={linkedFormId || ''}
                onChange={e => handleSelect(e.target.value)}
                disabled={loading}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors appearance-none bg-white"
              >
                <option value="">No linked form</option>
                {forms.map(f => (
                  <option key={f.formId} value={f.formId}>
                    {formatTypeLabel(f.formType)} - {f.status} - {new Date(f.updatedAt).toLocaleDateString()}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-500">
                {therapy
                  ? 'Select the F00139 Texas Therapies PA Request from the Forms module. The packet adds the plan of care and the evaluation (re-evaluation for a recertification).'
                  : 'Select from Forms module records of type CCP PA Request or CCP PA Reauthorization.'}
              </p>
              {loading && <p className="text-xs text-slate-500">Loading forms...</p>}
              {error && <p className="text-xs text-red-600">{error}</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FormAssociationSection;
